<template>
  <div class="fund-center">
    <!-- 规则说明 -->
    <div class="rule-tip">
      💡 货款结算规则：买家<b>确认收货即到账</b>；买家一直未确认收货时，商家发货后 <b>{{ summary.settle_days || 10 }} 天自动到账</b>。订单退款时，未结算货款将失效、已到账货款将从余额中扣减。
    </div>

    <!-- 资金概览 -->
    <div class="fund-grid">
      <div class="card main">
        <div class="val">￥{{ money(summary.balance) }}</div>
        <div class="lab">可用货款余额</div>
      </div>
      <div class="card">
        <div class="val frozen">￥{{ money(summary.frozen) }}</div>
        <div class="lab">在途货款（{{ summary.pending_count || 0 }} 笔待结算）</div>
      </div>
      <div class="card">
        <div class="val">￥{{ money(summary.coming_soon) }}</div>
        <div class="lab">7 天内即将到账</div>
      </div>
      <div class="card">
        <div class="val">￥{{ money(summary.total_income) }}</div>
        <div class="lab">累计到账货款</div>
      </div>
      <div class="card">
        <div class="val refund">￥{{ money(summary.total_refund) }}</div>
        <div class="lab">累计退款扣减</div>
      </div>
    </div>

    <!-- 流水筛选 -->
    <div class="fl-toolbar">
      <div class="filters">
        <button class="btn" :class="{ active: type === '' && status === '' }" @click="setFilter('', '')">全部流水</button>
        <button class="btn" :class="{ active: status === 'pending' }" @click="setFilter('', 'pending')">待结算</button>
        <button class="btn" :class="{ active: status === 'settled' }" @click="setFilter('income', 'settled')">已到账</button>
        <button class="btn" :class="{ active: type === 'refund' }" @click="setFilter('refund', '')">退款扣减</button>
        <button class="btn" :class="{ active: status === 'invalid' }" @click="setFilter('', 'invalid')">已失效</button>
      </div>
      <button class="btn primary" :disabled="syncing" @click="sync">{{ syncing ? '结算中...' : '⚡ 立即同步结算' }}</button>
    </div>

    <!-- 流水表格 -->
    <div class="fl-panel">
      <table class="fl-table" v-if="list.length > 0">
        <thead>
          <tr>
            <th style="width: 170px">时间</th>
            <th style="width: 170px">订单号</th>
            <th style="width: 110px">类型</th>
            <th style="width: 120px">金额</th>
            <th style="width: 140px">状态</th>
            <th style="width: 170px">到账 / 应到账时间</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in list" :key="row.id">
            <td>{{ row.created_at }}</td>
            <td>{{ row.order_no || '-' }}</td>
            <td>{{ row.type_text }}</td>
            <td :class="row.type === 'refund' ? 'minus' : 'plus'">
              {{ row.type === 'refund' ? '-' : '+' }}￥{{ money(row.amount) }}
            </td>
            <td>
              <span class="tag" :class="statusClass(row)">{{ row.status_text }}</span>
            </td>
            <td>{{ row.settled_at || row.due_at || '-' }}</td>
            <td class="remark">{{ row.remark }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="empty">暂无资金流水</div>

      <div class="fl-footer" v-if="list.length > 0">
        <span class="total-info">共 {{ totalPage || 1 }} 页 / {{ total }} 条流水</span>
        <div class="pager">
          <button class="btn" :disabled="page <= 1" @click="load(page - 1)">上一页</button>
          <span class="page-num">{{ page }} / {{ totalPage || 1 }}</span>
          <button class="btn" :disabled="page >= totalPage" @click="load(page + 1)">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFundSummaryApi, getFundFlowsApi, syncFundApi } from '@/api/merchant'

export default {
  name: 'FundCenter',
  data () {
    return {
      summary: {
        balance: 0,
        frozen: 0,
        pending_count: 0,
        coming_soon: 0,
        total_income: 0,
        total_refund: 0,
        settle_days: 10
      },
      list: [],
      page: 1,
      pageSize: 20,
      total: 0,
      totalPage: 1,
      type: '',
      status: '',
      syncing: false
    }
  },
  created () {
    this.loadSummary()
    this.load(1)
  },
  methods: {
    money (val) {
      const num = Number(val) || 0
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    statusClass (row) {
      if (row.type === 'refund') return 'refund'
      if (Number(row.status) === 1) return 'settled'
      if (Number(row.status) === 2) return 'invalid'
      return 'pending'
    },
    setFilter (type, status) {
      this.type = type
      this.status = status
      this.load(1)
    },
    async loadSummary () {
      try {
        const { data } = await getFundSummaryApi(0)
        if (data) {
          this.summary = data
        }
      } catch (e) {
        console.error('资金概览加载失败', e)
      }
    },
    async load (page) {
      this.page = Math.max(1, page || 1)
      try {
        const { data } = await getFundFlowsApi({
          page: this.page,
          pageSize: this.pageSize,
          type: this.type,
          status: this.status
        })
        this.list = (data && data.list) || []
        this.total = (data && data.total) || 0
        this.totalPage = (data && data.totalPage) || 1
      } catch (e) {
        console.error('资金流水加载失败', e)
      }
    },
    async sync () {
      this.syncing = true
      try {
        const { message } = await syncFundApi(0)
        if (message) alert(message)
        this.loadSummary()
        this.load(1)
      } catch (e) {
        console.error('结算同步失败', e)
      } finally {
        this.syncing = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.fund-center {
  .rule-tip {
    background: #fff7f0;
    border: 1px dashed #ffd2b0;
    color: #b06a00;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.7;
    margin-bottom: 14px;

    b { color: #ff6b35; }
  }

  .fund-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 14px;

    .card {
      background: #fff;
      border-radius: 10px;
      padding: 18px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

      &.main { background: linear-gradient(135deg, #ff6b35, #ff8c42); }
      &.main .val, &.main .lab { color: #fff; }

      .val {
        font-size: 22px;
        font-weight: 700;
        color: #222;

        &.frozen { color: #ff9800; }
        &.refund { color: #e53935; }
      }

      .lab { margin-top: 6px; font-size: 12px; color: #999; }
    }
  }

  .fl-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    background: #fff;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;

    .filters { display: flex; gap: 8px; flex-wrap: wrap; }
  }

  .btn {
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 6px;
    height: 32px;
    padding: 0 14px;
    cursor: pointer;
    color: #666;

    &.active { border-color: #ff6b35; color: #ff6b35; background: #fff7f0; }
    &.primary { background: linear-gradient(90deg, #ff6b35, #ff8c42); color: #fff; border: none; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .fl-panel {
    background: #fff;
    border-radius: 10px;
    padding: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }

  .fl-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td { padding: 9px 8px; border-bottom: 1px solid #f2f2f2; text-align: left; }
    th { color: #888; font-weight: 500; background: #fafafa; }
    .plus { color: #43a047; font-weight: 600; }
    .minus { color: #e53935; font-weight: 600; }
    .remark { color: #888; max-width: 320px; }

    .tag {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 10px;
      font-size: 12px;

      &.pending { background: #fff3e0; color: #f57c00; }
      &.settled { background: #e8f5e9; color: #43a047; }
      &.refund { background: #fdecea; color: #e53935; }
      &.invalid { background: #f0f0f0; color: #888; }
    }
  }

  .empty {
    text-align: center;
    color: #bbb;
    padding: 40px 0;
  }

  .fl-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;

    .total-info { font-size: 12px; color: #999; }
    .pager {
      display: flex;
      align-items: center;
      gap: 8px;
      .page-num { font-size: 12px; color: #666; }
    }
  }
}
</style>
